import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  IconAdd,
  IconChatbot,
  IconChevronDown,
  IconChevronUp,
  IconClose,
  IconDelete,
  IconEdit,
  IconError,
  IconFailed,
  IconInstant,
  IconList,
  IconMessage,
  IconPatients,
  IconPause,
  IconPending,
  IconPlay,
  IconRefresh,
  IconSave,
  IconSchedule,
  IconSearch,
  IconSmartSMS,
  IconSpinner,
  IconSuccess,
  IconVoiceCall,
  IconVoiceSMS,
} from '../components/icons';
import {
  EmptyState,
  StatusPill,
} from '../components/ui';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/Card';
import Button from '../components/Button';
import Alert from '../components/Alert';
import { useAuth } from '../context/AuthContext';
import { reviewAPI, scheduleAPI } from '../services/api';
import api from '../config/api';
import { cn } from '../utils/cn';
import { configRecipients, recipientCount as countRecipients } from '../utils/recipients';
import DeliveryRecord from '../components/DeliveryRecord';

// ── Channel type mapping ─────────────────────────────────────────────────────

const CHANNEL_META = {
  smart_sms: {
    label: 'Smart SMS',
    icon: IconSmartSMS,
    description: 'AI-powered SMS campaigns with automatic content generation',
    matchTypes: ['smart_sms'],
  },
  // The two voice descriptions used to be near-interchangeable -- "AI voice
  // message campaigns with natural speech synthesis" against "outbound voice
  // calls powered by conversational AI" -- which is why nobody could say what
  // the difference was. State it in terms of what the patient can do.
  voice_sms: {
    label: 'Voice SMS',
    icon: IconVoiceSMS,
    description: 'A recorded message plays down the phone. The patient listens; they cannot reply.',
    matchTypes: ['bulk_voice'],
    sendNow: { href: '/voice-sms/send', label: 'Send one now' },
  },
  voice_calls: {
    label: 'AI Voice Calls',
    icon: IconVoiceCall,
    description: 'A two-way conversation. The patient can ask questions and the agent answers.',
    matchTypes: ['elevenlabs_ivr'],
    sendNow: { href: '/voice-calls/bulk', label: 'Call a group now' },
  },
  chatbot: {
    label: 'AI Chatbot',
    icon: IconChatbot,
    description: 'AI health education chatbot',
    matchTypes: [],
  },
};

// Maps internal campaign_type to the channel_configs key in ScheduledCampaigns
const CAMPAIGN_TYPE_MAP = {
  smart_sms: 'smart_sms',
  voice_sms: 'bulk_voice',
  voice_calls: 'elevenlabs_ivr',
};

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'tw', label: 'Twi' },
  { value: 'ga', label: 'Ga' },
];

const STATUS_CONFIG = {
  active:    { label: 'Active',    tone: 'good' },
  paused:    { label: 'Paused',    tone: 'warn' },
  completed: { label: 'Completed', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'bad' },
  draft:     { label: 'Draft',     tone: 'neutral' },
};

const CADENCE_LABELS = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
  custom: 'Custom dates',
};

const WEEKDAY_LABELS = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };

// ── Helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return <StatusPill tone={cfg.tone}>{cfg.label}</StatusPill>;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatCadence(cadence) {
  if (!cadence) return '—';
  let text = CADENCE_LABELS[cadence.type] || cadence.type;
  if (cadence.time) text += ` at ${cadence.time}`;
  if ((cadence.type === 'weekly' || cadence.type === 'biweekly') && cadence.days?.length) {
    text += ` on ${cadence.days.map(d => WEEKDAY_LABELS[d] || d).join(', ')}`;
  }
  if (cadence.type === 'monthly' && cadence.day) {
    text += ` on the ${cadence.day}${cadence.day === 1 ? 'st' : cadence.day === 2 ? 'nd' : cadence.day === 3 ? 'rd' : 'th'}`;
  }
  return text;
}

// ── Patient Selector (mini) for editing ─────────────────────────────────────

function PatientSelectorMini({ selected, onChange }) {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get('/patients', { params: { user_id: user.id } })
      .then(({ data }) => {
        setPatients(data?.patients ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user]);

  const selectedPhones = new Set(selected);

  const filtered = patients.filter(p => {
    const q = search.toLowerCase();
    return !q || p.name?.toLowerCase().includes(q) || p.phone?.includes(q);
  });

  const toggle = (phone) => {
    if (selectedPhones.has(phone)) {
      onChange(selected.filter(p => p !== phone));
    } else {
      onChange([...selected, phone]);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center space-x-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
      >
        <IconPatients className="h-4 w-4" />
        <span>{selected.length} recipient{selected.length !== 1 ? 's' : ''}</span>
        {open ? <IconChevronUp className="h-3 w-3" /> : <IconChevronDown className="h-3 w-3" />}
      </button>
      {open && (
        <div className="mt-2 border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
            <div className="relative">
              <IconSearch className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
              <input
                className="input w-full pl-8 py-1.5 text-sm"
                placeholder="Search patients..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-gray-50">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <IconSpinner className="h-4 w-4 animate-spin text-gray-400" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No patients found.</p>
            ) : (
              filtered.map(p => (
                <div
                  key={p.id}
                  className={cn(
                    'flex items-center px-3 py-2 cursor-pointer transition-colors text-sm',
                    selectedPhones.has(p.phone) ? 'bg-primary-50' : 'hover:bg-gray-50',
                  )}
                  onClick={() => toggle(p.phone)}
                >
                  <input
                    type="checkbox"
                    readOnly
                    checked={selectedPhones.has(p.phone)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 mr-2"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{p.name}</p>
                    <p className="text-xs text-gray-400">{p.phone}{p.diagnosis ? ` · ${p.diagnosis}` : ''}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Channel config editor ────────────────────────────────────────────────────

function ConfigEditor({ channelType, config, onChange }) {
  const campaignType = CAMPAIGN_TYPE_MAP[channelType];

  if (campaignType === 'smart_sms') {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Health Topic</label>
          <input
            className="input w-full"
            value={config.health_topic || ''}
            onChange={e => onChange({ ...config, health_topic: e.target.value })}
            placeholder="Preeclampsia warning signs"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Goals</label>
          <textarea
            className="input w-full resize-none"
            rows={2}
            value={config.campaign_goals || ''}
            onChange={e => onChange({ ...config, campaign_goals: e.target.value })}
            placeholder="Educate about danger signs and when to seek care"
          />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
            <select
              className="input w-full"
              value={config.language || 'en'}
              onChange={e => onChange({ ...config, language: e.target.value })}
            >
              {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Messages</label>
            <input
              type="number"
              className="input w-full"
              min={1}
              max={5}
              value={config.max_messages_per_recipient || config.max_messages || 3}
              onChange={e => onChange({ ...config, max_messages_per_recipient: parseInt(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Delay (min)</label>
            <input
              type="number"
              className="input w-full"
              min={0}
              value={config.message_delay_minutes || 60}
              onChange={e => onChange({ ...config, message_delay_minutes: parseInt(e.target.value) })}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sender ID</label>
          <input
            className="input w-full"
            value={config.sender_id || 'CareArena'}
            onChange={e => onChange({ ...config, sender_id: e.target.value })}
            maxLength={11}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Recipients</label>
          <PatientSelectorMini
            selected={configRecipients(config)}
            onChange={recipients => onChange({ ...config, recipients })}
          />
        </div>
      </div>
    );
  }

  if (campaignType === 'bulk_voice') {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Disease / Condition</label>
          <input
            className="input w-full"
            value={config.disease_name || ''}
            onChange={e => onChange({ ...config, disease_name: e.target.value })}
            placeholder="Preeclampsia"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Goals</label>
          <textarea
            className="input w-full resize-none"
            rows={2}
            value={config.campaign_goals || ''}
            onChange={e => onChange({ ...config, campaign_goals: e.target.value })}
            placeholder="Monthly reminder about blood pressure monitoring"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
          <select
            className="input w-full"
            value={config.language || 'en'}
            onChange={e => onChange({ ...config, language: e.target.value })}
          >
            {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Recipients</label>
          <PatientSelectorMini
            selected={configRecipients(config)}
            onChange={recipients => onChange({ ...config, recipients })}
          />
        </div>
      </div>
    );
  }

  if (campaignType === 'elevenlabs_ivr') {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Health Topic</label>
          <input
            className="input w-full"
            value={config.health_topic || ''}
            onChange={e => onChange({ ...config, health_topic: e.target.value })}
            placeholder="Preeclampsia education"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Delay Between Calls (seconds)</label>
          <input
            type="number"
            className="input w-full"
            min={1}
            value={config.delay_between_calls ?? 2}
            // parseInt('') is NaN, and JSON.stringify writes NaN as null — so
            // clearing this field persisted delay_between_calls: null, which
            // the scheduler then fed to int() and crashed on before placing a
            // single call. Never store a non-number.
            onChange={e => {
              const parsed = parseInt(e.target.value, 10);
              onChange({
                ...config,
                delay_between_calls: Number.isFinite(parsed) ? Math.max(0, parsed) : 2,
              });
            }}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Recipients</label>
          <PatientSelectorMini
            selected={configRecipients(config)}
            onChange={recipients => onChange({ ...config, recipients })}
          />
        </div>
      </div>
    );
  }

  return null;
}

// ── Config display (read-only summary) ───────────────────────────────────────

function ConfigSummary({ channelType, config }) {
  const campaignType = CAMPAIGN_TYPE_MAP[channelType];
  const recipientCount = countRecipients(config);

  const fields = [];

  if (campaignType === 'smart_sms') {
    if (config.health_topic) fields.push({ label: 'Health Topic', value: config.health_topic });
    if (config.campaign_goals) fields.push({ label: 'Goals', value: config.campaign_goals });
    const lang = LANGUAGES.find(l => l.value === config.language);
    if (lang) fields.push({ label: 'Language', value: lang.label });
    fields.push({ label: 'Max Messages', value: String(config.max_messages_per_recipient || config.max_messages || 3) });
    fields.push({ label: 'Delay', value: `${config.message_delay_minutes || 60} min` });
    if (config.sender_id) fields.push({ label: 'Sender ID', value: config.sender_id });
  }

  if (campaignType === 'bulk_voice') {
    if (config.disease_name) fields.push({ label: 'Disease/Condition', value: config.disease_name });
    if (config.campaign_goals) fields.push({ label: 'Goals', value: config.campaign_goals });
    const lang = LANGUAGES.find(l => l.value === config.language);
    if (lang) fields.push({ label: 'Language', value: lang.label });
  }

  if (campaignType === 'elevenlabs_ivr') {
    if (config.health_topic) fields.push({ label: 'Health Topic', value: config.health_topic });
    fields.push({ label: 'Call Delay', value: `${config.delay_between_calls || 2}s` });
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2 text-sm text-primary-600">
        <IconPatients className="h-4 w-4" />
        <span className="font-medium">{recipientCount} recipient{recipientCount !== 1 ? 's' : ''}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
        {fields.map(f => (
          <div key={f.label} className="text-sm">
            <span className="text-gray-500">{f.label}: </span>
            <span className="text-gray-900 font-medium">{f.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Run history (mini) ───────────────────────────────────────────────────────

const RUN_STATUS_CONFIG = {
  pending:        { label: 'Pending',         icon: IconPending,       color: 'text-gray-500' },
  generating:     { label: 'Generating',      icon: IconSpinner,     color: 'text-blue-500 animate-spin' },
  pending_review: { label: 'Awaiting Review', icon: IconError, color: 'text-yellow-500' },
  running:        { label: 'Running',         icon: IconSpinner,     color: 'text-primary-500 animate-spin' },
  completed:      { label: 'Completed',       icon: IconSuccess, color: 'text-green-500' },
  failed:         { label: 'Failed',          icon: IconFailed,     color: 'text-red-500' },
  skipped:        { label: 'Skipped',         icon: IconPending,       color: 'text-gray-400' },
};

function RunHistoryMini({ programId }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    scheduleAPI.getRuns(programId, 5)
      .then(d => setRuns(d.runs || []))
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [programId]);

  if (loading) return <IconSpinner className="h-4 w-4 animate-spin text-gray-400" />;
  if (!runs.length) return <p className="text-xs text-gray-400">No runs yet</p>;

  return (
    <div className="space-y-1.5">
      {runs.map(run => {
        const cfg = RUN_STATUS_CONFIG[run.status] || RUN_STATUS_CONFIG.pending;
        const Icon = cfg.icon;
        return (
          <div key={run.id} className="flex items-center justify-between text-xs py-1.5 px-2.5 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-1.5">
              <Icon className={cn('h-3.5 w-3.5', cfg.color)} />
              <span className="text-gray-600">{cfg.label}</span>
            </div>
            <span className="text-gray-400">{formatDate(run.scheduled_at)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Campaign Program Card ────────────────────────────────────────────────────

/**
 * Channel tile shown at the head of each programme row.
 *
 * Borrowed from the shadcn Item primitive's `ItemMedia`: a fixed square that
 * anchors the row and says which channel you are looking at without your
 * having to read the heading. Tones match the dashboard activity feed, so a
 * channel keeps the same colour wherever it turns up.
 */
const CHANNEL_MEDIA = {
  smart_sms: { icon: IconSmartSMS, tile: 'bg-violet-50 text-violet-600 ring-violet-100' },
  voice_sms: { icon: IconVoiceSMS, tile: 'bg-sky-50 text-sky-600 ring-sky-100' },
  voice_calls: { icon: IconVoiceCall, tile: 'bg-emerald-50 text-emerald-600 ring-emerald-100' },
};

function ChannelMedia({ channelType }) {
  const media = CHANNEL_MEDIA[channelType] || CHANNEL_MEDIA.smart_sms;
  const Icon = media.icon;
  return (
    <span
      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ${media.tile}`}
    >
      <Icon size={18} aria-hidden="true" />
    </span>
  );
}

function ProgramCard({ program, channelType, onUpdate, onPause, onResume, onDelete, onRunNow, onShowDeliveries, awaitingApproval = 0 }) {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Extract the relevant config for this channel
  const getChannelConfig = () => {
    const cfg = program.campaign_config || {};
    const campaignType = CAMPAIGN_TYPE_MAP[channelType];
    // Multi-channel: find the right channel entry
    if (program.campaign_type === 'multi' && cfg.channels) {
      const channelEntry = cfg.channels.find(c => c.type === campaignType);
      return channelEntry || {};
    }
    // Single channel: config is flat
    return cfg;
  };

  const startEdit = () => {
    setEditing(true);
    setEditConfig({ ...getChannelConfig() });
    setEditName(program.name);
    setEditDesc(program.description || '');
    setError('');
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditConfig(null);
    setError('');
  };

  const saveEdit = async () => {
    setSaving(true);
    setError('');
    try {
      const campaignType = CAMPAIGN_TYPE_MAP[channelType];
      let newConfig;
      if (program.campaign_type === 'multi' && program.campaign_config?.channels) {
        // Update only this channel's entry in the multi-channel config
        const channels = program.campaign_config.channels.map(c =>
          c.type === campaignType ? { ...editConfig, type: campaignType } : c
        );
        newConfig = { ...program.campaign_config, channels };
      } else {
        newConfig = editConfig;
      }

      await scheduleAPI.update(program.id, {
        name: editName,
        description: editDesc,
        campaign_config: newConfig,
      });
      setEditing(false);
      onUpdate();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.userMessage || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const config = getChannelConfig();

  return (
    <Card className="overflow-hidden">
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Program Name</label>
                  <input
                    className="input w-full"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                  <input
                    className="input w-full"
                    value={editDesc}
                    onChange={e => setEditDesc(e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <ChannelMedia channelType={channelType} />
                <div className="min-w-0 flex-1">
                  {/* The badge sits with the actions on the right rather than
                      beside the heading: in a half-width card the heading wraps
                      to two lines and the badge was being pushed onto a third,
                      which read as a stray chip. */}
                  <h3 className="text-[15px] font-semibold leading-snug text-gray-900">
                    {program.name}
                  </h3>
                  {program.description && (
                    <p className="mt-0.5 text-sm text-gray-500">{program.description}</p>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="ml-3 flex flex-shrink-0 items-center">
            {!editing && <StatusBadge status={program.status} />}
          </div>
        </div>

        {/* Schedule info */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-4 pb-4 border-b border-gray-100">
          <span className="flex items-center space-x-1">
            <IconSchedule className="h-3.5 w-3.5" />
            <span>{formatCadence(program.cadence)}</span>
          </span>
          <span className="flex items-center space-x-1">
            <IconPending className="h-3.5 w-3.5" />
            <span>Next: <strong className="text-gray-700">{formatDate(program.next_run_at)}</strong></span>
          </span>
          <span>{program.run_count} run{program.run_count !== 1 ? 's' : ''}</span>
          {program.max_runs && <span>of {program.max_runs} max</span>}
        </div>

        {/* Campaign Config */}
        {editing ? (
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-700">Campaign Configuration</h4>
            <ConfigEditor
              channelType={channelType}
              config={editConfig}
              onChange={setEditConfig}
            />
            {error && (
              <div className="flex items-center space-x-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
                <IconError className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div className="flex items-center space-x-3 pt-2">
              <Button
                onClick={saveEdit}
                loading={saving}
                variant="primary"
                size="sm"
              >
                <IconSave className="h-4 w-4 mr-1.5" />
                Save Changes
              </Button>
              <button
                onClick={cancelEdit}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <ConfigSummary channelType={channelType} config={config} />
        )}

        {/* Run history and the row actions share the footer. The actions used
            to sit beside the heading, which in a half-width card left the name
            about a third of the row and wrapped "Monthly TB Adherence Check"
            onto three lines. */}
        <div className="mt-4 border-t border-gray-100 pt-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center space-x-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              {expanded ? <IconChevronUp className="h-3.5 w-3.5" /> : <IconChevronDown className="h-3.5 w-3.5" />}
              <span>Run History</span>
            </button>
            <div className="flex items-center gap-0.5">
              {!editing && (
                <button
                  onClick={startEdit}
                  title="Edit campaign"
                  className="p-2 rounded-lg text-gray-400 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  <IconEdit className="h-4 w-4" />
                </button>
              )}
              {/* "How many actually received it?" had no answer anywhere in
                  the product; general analytics only showed totals across
                  every campaign. */}
              <button
                onClick={() => onShowDeliveries(program.id)}
                title="Delivery record"
                className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-primary-600 transition-colors"
              >
                <IconList className="h-4 w-4" />
              </button>
              <button
                onClick={() => onRunNow(program.id)}
                title="Run now"
                className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-primary-600 transition-colors"
              >
                <IconInstant className="h-4 w-4" />
              </button>
              {program.status === 'active' ? (
                <button
                  onClick={() => onPause(program.id)}
                  title="Pause"
                  className="p-2 rounded-lg text-gray-400 hover:bg-yellow-50 hover:text-yellow-700 transition-colors"
                >
                  <IconPause className="h-4 w-4" />
                </button>
              ) : program.status === 'paused' ? (
                <button
                  onClick={() => onResume(program.id)}
                  title="Resume"
                  className="p-2 rounded-lg text-gray-400 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                >
                  <IconPlay className="h-4 w-4" />
                </button>
              ) : null}
              <button
                onClick={() => onDelete(program.id)}
                title="Cancel program"
                className="p-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <IconDelete className="h-4 w-4" />
              </button>
            </div>
          </div>
          {/* The campaign's own route into the queue. Finding out that THIS
              campaign was waiting previously meant opening the Review Queue
              and matching entries back to campaigns by name. */}
          {awaitingApproval > 0 && (
            <Link
              to="/review-queue"
              className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 transition-colors hover:bg-amber-100"
            >
              <span>
                <span className="font-semibold tabular-nums">{awaitingApproval}</span>
                {awaitingApproval === 1 ? ' message is' : ' messages are'} waiting for your approval
              </span>
              <span className="font-medium">Review →</span>
            </Link>
          )}

          {expanded && (
            <div className="mt-3">
              <RunHistoryMini programId={program.id} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── Empty State ──────────────────────────────────────────────────────────────

function ChannelEmptyState({ channelType }) {
  const navigate = useNavigate();
  const meta = CHANNEL_META[channelType];
  const Icon = meta?.icon || IconMessage;

  return (
    <EmptyState
      icon={Icon}
      title={`No ${meta?.label} campaigns yet`}
      description={`Create a program under Create Programs to set up recurring ${meta?.label} campaigns. You can edit and manage them here once they exist.`}
      className="py-20"
      action={
        <button
          onClick={() => navigate('/scheduled')}
          className="btn btn-primary inline-flex items-center gap-2 text-sm"
        >
          <IconAdd className="h-4 w-4" />
          <span>Go to Create Programs</span>
        </button>
      }
    />
  );
}

export default function ChannelCampaigns({ channelType }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id ?? null;

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  // Which campaign's delivery record is open, if any.
  const [deliveriesFor, setDeliveriesFor] = useState(null);
  // { [programId]: howManyWaiting } -- so a card can say that this campaign
  // in particular has content sitting in the queue, rather than leaving the
  // operator to match queue entries back to campaigns by name.
  const [awaiting, setAwaiting] = useState({});

  const meta = CHANNEL_META[channelType];
  const Icon = meta?.icon || IconMessage;
  // Stabilise the matchTypes array reference so the load callback below
  // doesn't get a new identity on every render — that previously caused
  // the useEffect dep to change every cycle, looping refetches forever.
  const matchTypes = useMemo(
    () => CHANNEL_META[channelType]?.matchTypes || [],
    [channelType]
  );

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await scheduleAPI.list(userId);
      const allPrograms = data.programs || [];
      // Filter programs that match this channel type
      const filtered = allPrograms.filter(p => {
        if (p.campaign_type === 'multi') {
          // Multi-channel: check if any channel matches
          const channels = p.campaign_config?.channels || [];
          return channels.some(c => matchTypes.includes(c.type));
        }
        return matchTypes.includes(p.campaign_type);
      });
      setPrograms(filtered);

      // Which of these campaigns have content sitting in the queue. Best
      // effort: a failure here must leave the campaign list working, just
      // without the badge.
      try {
        const queue = await reviewAPI.list({ status: 'pending', limit: 200 }, userId);
        const byProgram = {};
        for (const r of (queue?.reviews || [])) {
          const pid = r.metadata?.program_id || r.program_id;
          if (pid) byProgram[pid] = (byProgram[pid] || 0) + 1;
        }
        setAwaiting(byProgram);
      } catch {
        setAwaiting({});
      }
    } catch {
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  }, [userId, matchTypes]);

  useEffect(() => { load(); }, [load]);

  const handlePause = async (id) => {
    try { await scheduleAPI.pause(id); showToast('Program paused'); load(); }
    catch { showToast('Failed to pause', 'error'); }
  };

  const handleResume = async (id) => {
    try { await scheduleAPI.resume(id); showToast('Program resumed'); load(); }
    catch { showToast('Failed to resume', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Cancel this program? It will stop firing.')) return;
    try { await scheduleAPI.cancel(id); showToast('Program cancelled'); load(); }
    catch { showToast('Failed to cancel', 'error'); }
  };

  const handleRunNow = async (id) => {
    try { await scheduleAPI.runNow(id); showToast('Run triggered — campaign is being sent'); }
    catch { showToast('Failed to trigger run', 'error'); }
  };

  const activePrograms = programs.filter(p => p.status === 'active');
  const otherPrograms = programs.filter(p => p.status !== 'active');

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center space-x-2',
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-900 text-white',
        )}>
          {toast.type === 'error' ? <IconFailed className="h-4 w-4" /> : <IconSuccess className="h-4 w-4" />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Icon className="mr-2 h-6 w-6 shrink-0 text-primary-600" />
            {meta?.label} Campaigns
          </h2>
          {/* Capped so the sentence wraps at a readable measure instead of
              running the full width and squeezing the actions into two-line
              buttons. */}
          <p className="text-gray-600 mt-1 max-w-prose">{meta?.description}</p>
        </div>
        <div className="flex shrink-0 items-center space-x-2 whitespace-nowrap">
          <button
            onClick={load}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            title="Refresh"
          >
            <IconRefresh className={cn('h-4 w-4', loading && 'animate-spin')} />
          </button>
          {/* Sending immediately used to be its own rail entry, which is how
              one channel ended up looking like two. It belongs here, beside
              that channel's scheduled campaigns. */}
          {meta?.sendNow && (
            <button
              onClick={() => navigate(meta.sendNow.href)}
              className="btn btn-secondary whitespace-nowrap"
            >
              {meta.sendNow.label}
            </button>
          )}
          <button
            onClick={() => navigate('/scheduled')}
            className="btn btn-primary flex items-center space-x-2 whitespace-nowrap"
          >
            <IconAdd className="h-4 w-4" />
            <span>New Program</span>
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <IconSpinner className="h-7 w-7 animate-spin text-primary-500" />
        </div>
      ) : programs.length === 0 ? (
        <ChannelEmptyState channelType={channelType} />
      ) : (
        <div className="space-y-6">
          {activePrograms.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Active ({activePrograms.length})
              </h3>
              {/* Two up from xl. A programme is a heading, a schedule line and a
                  short config summary; at full width most of the row was empty.
                  items-start stops a card that has been expanded from
                  stretching its neighbour to match. */}
              <div className="grid items-start gap-4 xl:grid-cols-2">
                {activePrograms.map(p => (
                  <ProgramCard
                    key={p.id}
                    program={p}
                    channelType={channelType}
                    onUpdate={load}
                    onPause={handlePause}
                    onResume={handleResume}
                    onDelete={handleDelete}
                    onRunNow={handleRunNow}
                    onShowDeliveries={setDeliveriesFor}
                    awaitingApproval={awaiting[p.id] || 0}
                  />
                ))}
              </div>
            </section>
          )}
          {otherPrograms.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Past & Paused ({otherPrograms.length})
              </h3>
              <div className="grid items-start gap-4 opacity-80 xl:grid-cols-2">
                {otherPrograms.map(p => (
                  <ProgramCard
                    key={p.id}
                    program={p}
                    channelType={channelType}
                    onUpdate={load}
                    onPause={handlePause}
                    onResume={handleResume}
                    onDelete={handleDelete}
                    onRunNow={handleRunNow}
                    onShowDeliveries={setDeliveriesFor}
                    awaitingApproval={awaiting[p.id] || 0}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
      {deliveriesFor && (
        <DeliveryRecord
          programId={deliveriesFor}
          onClose={() => setDeliveriesFor(null)}
        />
      )}
    </div>
  );
}
